import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon,
  ArrowBack as BackIcon,
  CheckCircle,
  Info
} from '@mui/icons-material';
import Papa from 'papaparse';
import api from '../axiosConfig';

const ProductImport = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  const templateData = [
    {
      name: 'Hair Spray',
      description: 'Professional hold hair spray',
      barcode: '5012345678900',
      pack_size: '300',
      unit_type: 'ml',
      current_cost_price: '8.50',
      retail_price: '12.99',
      vat_rate: '20.00',
      reorder_level: '5',
      reorder_quantity: '10',
      category: 'Hair Products',
      supplier: 'Beauty Supplies Ltd'
    },
    {
      name: 'Shampoo',
      description: 'Moisturizing shampoo',
      barcode: '5012345678901',
      pack_size: '500',
      unit_type: 'ml',
      current_cost_price: '6.00',
      retail_price: '9.99',
      vat_rate: '20.00',
      reorder_level: '10',
      reorder_quantity: '20',
      category: 'Hair Products',
      supplier: 'Beauty Supplies Ltd'
    }
  ];

  const handleDownloadTemplate = () => {
    const csv = Papa.unparse(templateData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'product_import_template.csv';
    link.click();
  };

  const validateRow = (row, rowIndex) => {
    const errors = [];

    // Required fields
    if (!row.name || row.name.trim() === '') {
      errors.push(`Row ${rowIndex + 2}: Product name is required`);
    }

    if (!row.current_cost_price || isNaN(parseFloat(row.current_cost_price))) {
      errors.push(`Row ${rowIndex + 2}: Valid cost price is required`);
    }

    if (!row.unit_type || row.unit_type.trim() === '') {
      errors.push(`Row ${rowIndex + 2}: Unit type is required (e.g., ml, g, uses)`);
    }

    // Validate numeric fields
    const numericFields = [
      'pack_size',
      'current_cost_price',
      'retail_price',
      'vat_rate',
      'reorder_level',
      'reorder_quantity'
    ];

    numericFields.forEach(field => {
      if (row[field] && isNaN(parseFloat(row[field]))) {
        errors.push(`Row ${rowIndex + 2}: ${field} must be a valid number`);
      }
    });

    // Validate VAT rate
    if (row.vat_rate) {
      const vatRate = parseFloat(row.vat_rate);
      if (vatRate < 0 || vatRate > 100) {
        errors.push(`Row ${rowIndex + 2}: VAT rate must be between 0 and 100`);
      }
    }

    return errors;
  };

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setIsProcessing(true);
    setValidationErrors([]);
    setImportResults(null);
    setMessage('');

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: 'greedy',
      transform: (value) => value.trim(),
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: async (results) => {
        try {
          if (results.errors.length > 0) {
            setMessageType('error');
            setMessage('Error parsing CSV file. Please check the format.');
            setIsProcessing(false);
            return;
          }

          if (!results.data || results.data.length === 0) {
            setMessageType('error');
            setMessage('No data found in CSV file');
            setIsProcessing(false);
            return;
          }

          // Validate all rows
          const allErrors = [];
          results.data.forEach((row, index) => {
            const rowErrors = validateRow(row, index);
            allErrors.push(...rowErrors);
          });

          if (allErrors.length > 0) {
            setValidationErrors(allErrors);
            setMessageType('error');
            setMessage(`Found ${allErrors.length} validation error(s). Please fix and try again.`);
            setIsProcessing(false);
            return;
          }

          // Process and upload
          const formData = new FormData();
          formData.append('file', selectedFile);

          const response = await api.post('/csv/import/products', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          const { success = 0, updated = 0 } = response.data;
          setImportResults(response.data);
          setMessageType(success > 0 || updated > 0 ? 'success' : 'error');
          setMessage(
            success > 0 || updated > 0
              ? `Successfully imported ${success} new product(s), updated ${updated} existing product(s)`
              : 'Import failed. Please check errors below.'
          );

        } catch (error) {
          console.error('Upload error:', error);
          setMessageType('error');
          setMessage(
            error.response?.data?.error ||
            'Error uploading file. Please try again.'
          );
        } finally {
          setIsProcessing(false);
          event.target.value = '';
        }
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        setMessageType('error');
        setMessage('Error reading CSV file. Please check the format.');
        setIsProcessing(false);
        event.target.value = '';
      }
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/import')}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h4" fontWeight="bold">
            Import Products
          </Typography>
        </Box>

        <Paper>
          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} variant="fullWidth">
            <Tab label="Instructions" />
            <Tab label="Import" />
          </Tabs>

          {/* Instructions Tab */}
          {currentTab === 0 && (
            <Box sx={{ p: 4 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Before You Start
                </Typography>
                <Typography variant="body2">
                  Download the template, fill in your product data, and upload the completed CSV file.
                </Typography>
              </Alert>

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Required Fields
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="name"
                    secondary="Product name (e.g., Hair Spray, Shampoo)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="current_cost_price"
                    secondary="Your cost price (e.g., 8.50)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="unit_type"
                    secondary="Unit of measurement (ml, g, uses, etc.)"
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Optional Fields
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText
                    primary="description"
                    secondary="Product description"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText
                    primary="barcode"
                    secondary="Product barcode/SKU"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText
                    primary="pack_size"
                    secondary="Size of product (e.g., 300 for 300ml)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText
                    primary="retail_price"
                    secondary="Retail price (defaults to cost price if not provided)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText
                    primary="vat_rate"
                    secondary="VAT percentage (defaults to 20%)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText
                    primary="reorder_level"
                    secondary="Stock level to trigger reorder alert"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText
                    primary="reorder_quantity"
                    secondary="Quantity to reorder when stock is low"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText
                    primary="category"
                    secondary="Product category name"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText
                    primary="supplier"
                    secondary="Supplier name"
                  />
                </ListItem>
              </List>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Cost prices should be entered without currency symbols (e.g., 8.50 not £8.50).
                  Categories and suppliers will be created if they don't exist.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Import Tab */}
          {currentTab === 1 && (
            <Box sx={{ p: 4 }}>
              {message && (
                <Alert severity={messageType} sx={{ mb: 3 }}>
                  {message}
                </Alert>
              )}

              {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Validation Errors:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                    {validationErrors.slice(0, 10).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                    {validationErrors.length > 10 && (
                      <li>...and {validationErrors.length - 10} more errors</li>
                    )}
                  </Box>
                </Alert>
              )}

              {importResults && (
                <Alert
                  severity={
                    (importResults.success > 0 || importResults.updated > 0) && importResults.failures.length === 0
                      ? 'success'
                      : (importResults.success > 0 || importResults.updated > 0)
                        ? 'warning'
                        : 'error'
                  }
                  sx={{ mb: 3 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Import Results:
                  </Typography>
                  <ul>
                    <li>Successfully imported: {importResults.success}</li>
                    {importResults.updated > 0 && (
                      <li>Updated existing: {importResults.updated}</li>
                    )}
                    {importResults.failures && importResults.failures.length > 0 && (
                      <li>Failed imports: {importResults.failures.length}</li>
                    )}
                    {importResults.duplicates && importResults.duplicates > 0 && (
                      <li>Duplicates skipped: {importResults.duplicates}</li>
                    )}
                  </ul>
                  {importResults.failures && importResults.failures.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" fontWeight="bold">
                        Errors:
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                        {importResults.failures.slice(0, 5).map((failure, index) => (
                          <li key={index}>{failure}</li>
                        ))}
                        {importResults.failures.length > 5 && (
                          <li>...and {importResults.failures.length - 5} more errors</li>
                        )}
                      </Box>
                    </Box>
                  )}
                </Alert>
              )}

              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                onClick={handleDownloadTemplate}
                sx={{ mb: 3 }}
                disabled={isProcessing}
              >
                Download Template
              </Button>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Upload CSV File
              </Typography>

              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="product-csv-input"
                disabled={isProcessing}
              />
              <label htmlFor="product-csv-input">
                <Button
                  startIcon={isProcessing ? <CircularProgress size={20} /> : <UploadIcon />}
                  variant="contained"
                  component="span"
                  disabled={isProcessing}
                  size="large"
                >
                  {isProcessing ? 'Processing...' : 'Choose CSV File'}
                </Button>
              </label>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ProductImport;